import { Button, Input, Modal, Spin, Typography, message } from "antd";
import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery } from "react-query";
import { appInject } from "@core/di/utils";
import { DI_TOKENS } from "@shared/constants/di";
import { IPlayerCardService } from "@shared/interfaces/player-card-service.interface";
import styles from "./SetTeamJerseyColorsModal.module.scss";

interface SetTeamJerseyColorsModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const HEX6 = /^#[0-9A-Fa-f]{6}$/;

const SetTeamJerseyColorsModal = ({
  open,
  onClose,
  onSuccess,
}: SetTeamJerseyColorsModalProps) => {
  const [colorsByTeam, setColorsByTeam] = useState<Record<string, string>>({});
  const hasInitializedRef = useRef(false);

  const playerCardsService = appInject<IPlayerCardService>(
    DI_TOKENS.playerCardService,
  );

  const teamsQuery = useQuery(
    ["player-card-teams-jersey-colors"],
    () => playerCardsService.getTeams(),
    {
      enabled: open,
      refetchOnMount: true,
    },
  );

  const teams = useMemo(
    () => teamsQuery.data?.data.teams || [],
    [teamsQuery.data?.data.teams],
  );

  const sortedTeams = useMemo(
    () =>
      [...teams].sort((a, b) =>
        (a.key || a.team).localeCompare(b.key || b.team),
      ),
    [teams],
  );

  useEffect(() => {
    if (!open) {
      setColorsByTeam({});
      hasInitializedRef.current = false;
      return;
    }

    if (hasInitializedRef.current || teams.length === 0) {
      return;
    }

    const initial: Record<string, string> = {};
    for (const team of teams) {
      const color = team.jerseyNumberColor?.trim();
      if (color) {
        initial[team.team] = color;
      }
    }
    setColorsByTeam(initial);
    hasInitializedRef.current = true;
  }, [open, teams]);

  const setTeamColor = (teamName: string, value: string) => {
    setColorsByTeam((prev) => ({ ...prev, [teamName]: value }));
  };

  const teamsInPayload = useMemo(
    () =>
      sortedTeams.filter((team) =>
        Object.prototype.hasOwnProperty.call(colorsByTeam, team.team),
      ),
    [sortedTeams, colorsByTeam],
  );

  const hasInvalidColor = teamsInPayload.some((team) => {
    const color = (colorsByTeam[team.team] || "").trim();
    return color.length > 0 && !HEX6.test(color);
  });

  const applyMutation = useMutation(
    () => {
      const payload = teamsInPayload.map((team) => ({
        team: team.team,
        jerseyNumberColor: (colorsByTeam[team.team] || "").trim(),
      }));

      return playerCardsService.applyJerseyNumberColorsByTeam({
        teams: payload,
      });
    },
    {
      onSuccess: (response) => {
        const result = response?.data;
        void message.success(
          result
            ? `Updated ${result.totalUpdated} cards across ${result.totalTeamsApplied} teams.`
            : "Jersey number colors applied.",
        );
        onSuccess();
      },
      onError: () => {
        void message.error("Failed to apply jersey number colors by team");
      },
    },
  );

  const teamsToApply = teamsInPayload.length;

  return (
    <Modal
      title="Set jersey number colors by team"
      open={open}
      onCancel={onClose}
      width={720}
      destroyOnClose
      className={styles.modal}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button
          key="apply"
          type="primary"
          disabled={
            teamsToApply === 0 || hasInvalidColor || teamsQuery.isLoading
          }
          loading={applyMutation.isLoading}
          onClick={() => applyMutation.mutate()}
        >
          Apply to {teamsToApply || 0} team{teamsToApply === 1 ? "" : "s"}
        </Button>,
      ]}
    >
      <Typography.Paragraph type="secondary" className={styles.hint}>
        Teams sorted by code. Existing colors are prefilled. Clear sends an
        empty color to remove a saved team color. Invalid hex blocks apply.
      </Typography.Paragraph>

      <Spin spinning={teamsQuery.isLoading}>
        <div className={styles.scrollBody}>
          <div className={styles.teamGrid}>
            {sortedTeams.map((team) => {
              const color = colorsByTeam[team.team] || "";
              const hasColor = color.trim().length > 0;
              const pickerValue = HEX6.test(color) ? color : "#ffffff";
              const teamCode = team.key || team.team;

              return (
                <div
                  key={teamCode}
                  className={`${styles.teamRow}${
                    hasColor ? ` ${styles.teamRowActive}` : ""
                  }`}
                >
                  <span className={styles.teamCode} title={team.team}>
                    {teamCode}
                  </span>

                  <div className={styles.colorControls}>
                    <div
                      className={styles.swatch}
                      style={{
                        backgroundColor: hasColor ? pickerValue : "#fafafa",
                      }}
                    >
                      <input
                        type="color"
                        value={pickerValue}
                        className={styles.hiddenPicker}
                        aria-label={`${team.team} jersey number color picker`}
                        onChange={(event) =>
                          setTeamColor(team.team, event.target.value)
                        }
                      />
                    </div>
                    <Input
                      value={color}
                      placeholder="#hex"
                      maxLength={50}
                      size="small"
                      className={styles.hexInput}
                      aria-label={`${team.team} jersey number color`}
                      onChange={(event) =>
                        setTeamColor(team.team, event.target.value)
                      }
                    />
                    {hasColor ? (
                      <Button
                        type="link"
                        size="small"
                        className={styles.clearBtn}
                        onClick={() => setTeamColor(team.team, "")}
                      >
                        Clear
                      </Button>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </Spin>
    </Modal>
  );
};

export default SetTeamJerseyColorsModal;
